import type { LucideIcon } from 'lucide-react';
import { SectionIcon } from './SectionIcon.js';
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import {
  STAGES,
  STAGE_LABELS,
  type Stage,
  type StageFilterState,
  type StageMode,
} from '@nemo/core/model';
import { MultiSelect } from './MultiSelect.js';
import { SegmentedControl, type Segment } from './SegmentedControl.js';
import type { SelectOption } from './Select.js';

/**
 * The one Filters dialog every list shares: the funds grid and the Dealroom
 * candidates each fill it with their own groups, and each reads its active
 * values back as the same row of removable chips under the toolbar.
 */

export type { StageMode };

/** Where the stage filter's two modes read differently: "Leads at Series A". */
export function stageFilterLabel(mode: StageMode, stage: Stage | null): string {
  const verb = mode === 'leads' ? 'Leads at' : 'Invests at';
  return stage === null ? `${verb} any stage` : `${verb} ${STAGE_LABELS[stage]}`;
}

/** A reduced stage filter as the fields the lists keep it in. */
export function stageFieldsOf(state: StageFilterState): { stageMode: StageMode; stages: Stage[] } {
  return { stageMode: state.mode, stages: [...state.stages] };
}

/** One active filter value as the summary row shows it, and how to take it away. */
export interface FilterChip<F> {
  readonly key: string;
  readonly label: string;
  readonly remove: (filters: F) => F;
}

/** Adds or removes one value, keeping the options' own order. */
export function toggled(
  current: readonly string[],
  value: string,
  options: readonly SelectOption[],
): string[] {
  return current.includes(value)
    ? current.filter((existing) => existing !== value)
    : options.map((option) => option.value).filter((v) => v === value || current.includes(v));
}

export interface FilterDialogFrameProps {
  /** Whether anything is active, which is what Clear all can act on. */
  canClear: boolean;
  onClear: () => void;
  onClose: () => void;
  /** The close button's words: "Show 12 funds". */
  showLabel: string;
  children: React.ReactNode;
}

/**
 * The dialog itself: a change applies at once, so the count on the close
 * button is the answer to the filters as they stand.
 */
export function FilterDialogFrame(props: FilterDialogFrameProps): React.JSX.Element {
  const titleId = useId();
  const { onClose } = props;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      onClose();
    }
    // Bubble rather than capture: an open select inside the dialog closes
    // first and marks the event handled, so one Escape is one step back.
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal filter-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="filter-dialog-head">
          <h2 id={titleId} className="modal-title">
            Filters
          </h2>
          <button
            className="button small"
            type="button"
            disabled={!props.canClear}
            onClick={props.onClear}
          >
            Clear all
          </button>
        </div>

        {props.children}

        <div className="modal-actions">
          <button className="button primary" type="button" onClick={onClose}>
            {props.showLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function FilterGroup(props: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="filter-group">
      <h3 className="filter-group-title">
        <SectionIcon icon={props.icon} />
        {props.title}
      </h3>
      {props.children}
    </section>
  );
}

export function FilterRow(props: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="filter-row">
      <span className="filter-row-label">{props.label}</span>
      <span className="filter-row-controls">{props.children}</span>
    </div>
  );
}

const STAGE_OPTIONS: readonly SelectOption[] = STAGES.map((stage) => ({
  value: stage,
  label: STAGE_LABELS[stage],
}));

const STAGE_MODES: readonly Segment<StageMode>[] = [
  { value: 'invests', label: 'Invests at' },
  { value: 'leads', label: 'Leads at' },
];

/** Stages, asked one of two ways: invests at, or leads at. */
export function StageFilter(props: {
  mode: StageMode;
  stages: readonly Stage[];
  onChange: (change: { mode?: StageMode; stages?: Stage[] }) => void;
}): React.JSX.Element {
  return (
    <FilterRow label="Stage">
      <SegmentedControl
        label="Invests at or leads at"
        value={props.mode}
        options={STAGE_MODES}
        onChange={(mode) => props.onChange({ mode })}
      />
      <MultiSelect
        label="Stage"
        noun="stages"
        placeholder="Every stage"
        options={STAGE_OPTIONS}
        selected={props.stages}
        onToggle={(value) =>
          props.onChange({
            stages: STAGES.filter((stage) =>
              stage === value ? !props.stages.includes(stage) : props.stages.includes(stage),
            ),
          })
        }
        onClear={() => props.onChange({ stages: [] })}
      />
    </FilterRow>
  );
}

/**
 * What the filters are doing, under the toolbar: one removable chip a value,
 * Clear all, and whatever the list adds at the end of the row.
 */
export function ActiveFilters<F>(props: {
  chips: readonly FilterChip<F>[];
  onChange: (update: (filters: F) => F) => void;
  onClearAll: () => void;
  children?: React.ReactNode;
}): React.JSX.Element | null {
  if (props.chips.length === 0) return null;
  return (
    <div className="filter-summary" aria-label="Active filters">
      {props.chips.map((chip) => (
        <span className="filter-chip" key={chip.key}>
          {chip.label}
          <button
            className="filter-chip-remove"
            type="button"
            aria-label={`Remove ${chip.label}`}
            onClick={() => props.onChange(chip.remove)}
          >
            ×
          </button>
        </span>
      ))}
      <button className="link-button" type="button" onClick={props.onClearAll}>
        Clear all
      </button>
      {props.children}
    </div>
  );
}
